import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations } from '../translations';

export type Language = 'en' | 'zh';


interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode; initialLanguage?: Language }> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguage] = useState<Language>(initialLanguage ?? 'zh');

  useEffect(() => {
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');

    const getValue = (lang: Language) => {
      let current: any = translations[lang];
      for (const k of keys) {
        if (current && typeof current === 'object' && k in current) {
          current = current[k as keyof typeof current];
        } else {
          return undefined;
        }
      }
      return current;
    };

    const value = getValue(language);

    const applyParams = (text: string) => {
      if (!params) {
        return text;
      }
      return text.replace(/\{(\w+)\}/g, (match, token) => {
        if (Object.prototype.hasOwnProperty.call(params, token)) {
          return String(params[token]);
        }
        return match;
      });
    };

    // Return translation if found and is a string
    if (typeof value === 'string') {
      return applyParams(value);
    }

    // Fallback to English
    const enValue = getValue('en');
    if (typeof enValue === 'string') {
      return applyParams(enValue);
    }

    // Fallback to key if nothing found
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
